import React, { FC, useState, useCallback, useEffect, useMemo } from 'react'
import { ApexOptions } from 'apexcharts'
import { format } from 'date-fns'
import { Alert, Box, CircularProgress, Grid, IconButton, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material'
import ArrowBackIcon from '@mui/icons-material/ArrowBackRounded'
import ArrowForwardIcon from '@mui/icons-material/ArrowForwardRounded'
import CloseIcon from '@mui/icons-material/CloseRounded'
import { useDevicesCountByHourRequest, useNetworkOutagesRequest } from 'src/hooks/api'
import { Chart } from 'src/components/ui'
import { DevicesCountByHourItem, NetworkOutageItem } from 'src/api'
import {
  DEVICES_COUNT_COLOR,
  DEVICES_COUNT_IN_PROGRESS_COLOR,
  DevicesCountPoint,
  OUTAGE_COLOR,
  OutageChartPoint,
  OutagePointMeta,
  buildDevicesCountSeriesData,
  buildOutageAnnotationRanges,
  buildOutageSeriesData,
  buildOutageTooltipHtml,
  utcHourKey
} from './networkOutagePresentation'
import { NetworkOutageDownloadLink } from './networkOutageDownloadLink'
import { NetworkOutagesTable } from './networkOutagesTable'

type SensorType = 'verizon' | 'lorawan'

const STEP = 7

// positional series contract: every per-series array in chartOptions (colors, stroke, markers) follows this order
const DEVICES_SERIES_INDEX = 0
const OUTAGE_SERIES_INDEX = 1
// the current, still-collecting hour is drawn as a dotted grey tail: apexcharts' forecastDataPoints dashes the last
// segment of the devices series and renders it as a clone clipped by its "forecastMask", which this selector recolors
const IN_PROGRESS_SEGMENT_SELECTOR = '& path[clip-path*="forecastMask"]'

export const DevicesCountByHourChart: FC = () => {
  const [dateFrom, setDateFrom] = useState<Date>(new Date(new Date().setDate(new Date().getDate() - STEP)))
  const [dateTo, setDateTo] = useState<Date>(new Date())
  const [sensorType, setSensorType] = useState<SensorType>('verizon')
  const [devicesCountList, setDevicesCountList] = useState<DevicesCountByHourItem[]>([])
  const [outagesList, setOutagesList] = useState<NetworkOutageItem[]>([])
  const [selectedOutage, setSelectedOutage] = useState<OutagePointMeta | null>(null)

  // request exactly the STEP*24 hour buckets the chart grid renders: dateFrom = the earliest grid hour
  // (derived from dateTo, ms zeroed) so the API can never return a bucket the grid drops
  const requestParams = {
    dateFrom: new Date(new Date(dateTo).setUTCHours(new Date(dateTo).getUTCHours() - (STEP * 24 - 1), 0, 0, 0)).toISOString(),
    dateTo: new Date(new Date(dateTo).setUTCMinutes(59, 59)).toISOString(),
    sensorType,
  }

  const {
    data: devicesCountData,
    isInitialLoading,
    isRefetching,
    refetch: refetchDevicesCount,
  } = useDevicesCountByHourRequest({
    params: requestParams,
    options: { enabled: false },
  })

  const {
    data: outagesData,
    isInitialLoading: isOutagesInitialLoading,
    isRefetching: isOutagesRefetching,
    refetch: refetchOutages,
  } = useNetworkOutagesRequest({
    params: requestParams,
    options: { enabled: false },
  })

  // the chart renders both datasets, so its loading/nav gating must wait for both queries
  const isChartLoading = isInitialLoading || isOutagesInitialLoading
  const isAnyRefetching = isRefetching || isOutagesRefetching

  useEffect(() => {
    setDevicesCountList(devicesCountData || [])
  }, [devicesCountData])

  useEffect(() => {
    setOutagesList(outagesData || [])
  }, [outagesData])

  useEffect(() => {
    if (dateFrom && dateTo) {
      setSelectedOutage(null)
      refetchDevicesCount()
      refetchOutages()
    }
  }, [dateFrom, dateTo, sensorType, refetchDevicesCount, refetchOutages])

  const isLastPeriod = useCallback(() => {
    return dateTo.getDate() === new Date().getDate()
      && dateTo.getMonth() === new Date().getMonth()
      && dateTo.getFullYear() === new Date().getFullYear()
  }, [dateTo])()

  const handleGoBack = () => {
    if (isAnyRefetching) return

    const newTimeFrom = new Date(new Date(dateFrom).setDate(new Date(dateFrom).getDate() - STEP))
    const newTimeTo = new Date(new Date(dateTo).setDate(new Date(dateTo).getDate() - STEP))
    setDateFrom(newTimeFrom)
    setDateTo(newTimeTo)
  }

  const handleGoForward = () => {
    if (isLastPeriod || isAnyRefetching) return

    const newTimeFrom = new Date(new Date(dateFrom).setDate(new Date(dateFrom).getDate() + STEP))
    const newTimeTo = new Date(new Date(dateTo).setDate(new Date(dateTo).getDate() + STEP))
    setDateFrom(newTimeFrom)
    setDateTo(newTimeTo)
  }

  const resetRange = () => {
    const newTimeFrom = new Date(new Date(new Date().setDate(new Date().getDate() - STEP)))
    const newTimeTo = new Date()
    setDateFrom(newTimeFrom)
    setDateTo(newTimeTo)
  }

  const handleSensorTypeChange = (_: React.MouseEvent<HTMLElement>, newSensorType: SensorType | null) => {
    if (newSensorType !== null) {
      setSensorType(newSensorType)
    }
  }

  const outageSeriesData = useMemo(() => buildOutageSeriesData(dateTo, STEP * 24, outagesList), [dateTo, outagesList])

  const devicesCountSeries = useMemo(() => {
    const deviceCountByHour = new Map<number, number>()
    devicesCountList.forEach((entry) => {
      if (entry.datetime) deviceCountByHour.set(utcHourKey(entry.datetime), entry.deviceCount ?? 0)
    })
    return buildDevicesCountSeriesData(dateTo, STEP * 24, deviceCountByHour)
  }, [devicesCountList, dateTo])

  const getChartData = useCallback(
    (): ApexOptions['series'] => {
      // order must match DEVICES_SERIES_INDEX / OUTAGE_SERIES_INDEX
      return [
        {
          name: 'Devices count',
          data: devicesCountSeries.data,
        },
        {
          name: 'Network outages',
          data: outageSeriesData,
        },
      ]
    },
    [devicesCountSeries, outageSeriesData]
  )

  const chartOptions: ApexOptions = {
    chart: {
      type: 'line',
      events: {
        dataPointSelection: (_event, chart, { seriesIndex, dataPointIndex }) => {
          if (seriesIndex !== OUTAGE_SERIES_INDEX) return
          const point = chart.w.globals.initialSeries[OUTAGE_SERIES_INDEX]?.data[dataPointIndex] as OutageChartPoint | undefined
          const meta = point?.meta
          // deferred: a synchronous state update re-renders the chart while apexcharts is still handling the click
          if (meta) setTimeout(() => setSelectedOutage(meta), 0)
        },
      },
      toolbar: {
        tools: {
          download: true,
          selection: false,
          zoom: false,
          zoomin: false,
          zoomout: false,
          pan: false,
          reset: false,
        },
        export: {
          csv: {
            headerCategory: 'Time',
          },
        },
      },
    },
    colors: [DEVICES_COUNT_COLOR, OUTAGE_COLOR],
    stroke: {
      // the outage series must stay 'straight': smooth curves ignore null gaps and connect isolated outages
      curve: ['smooth', 'straight'],
      width: [3, 3],
    },
    // dashes the final segment (last complete hour -> in-progress hour); the outage series ends with null so it is unaffected.
    // The segment is only a few px wide on a 7-day axis, so short dashes would blur into a solid line.
    forecastDataPoints: {
      count: devicesCountSeries.endsInProgress ? 1 : 0,
      dashArray: 6,
      strokeWidth: 3,
    },
    markers: {
      // must stay 0 for every series: any per-series size > 0 makes apexcharts position the shared tooltip from real
      // marker elements, which only exist on outage hours, so the tooltip and crosshair freeze everywhere else
      size: 0,
      strokeWidth: 0,
      hover: {
        sizeOffset: 2,
      },
      // the outage dots are drawn as discrete markers instead
      discrete: outageSeriesData.flatMap((point, dataPointIndex) =>
        point.meta ? [{ seriesIndex: OUTAGE_SERIES_INDEX, dataPointIndex, size: 5, fillColor: OUTAGE_COLOR, strokeColor: OUTAGE_COLOR }] : []
      ),
    },
    annotations: {
      xaxis: buildOutageAnnotationRanges(outageSeriesData).map(({ x, x2 }) => ({
        x,
        x2,
        fillColor: OUTAGE_COLOR,
        opacity: 0.08,
        borderColor: 'transparent',
      })),
    },
    dataLabels: {
      enabled: false,
    },
    legend: {
      // legend toggling is unsupported: the custom tooltip and the xaxis annotation bands don't track collapsed series
      show: false,
    },
    xaxis: {
      type: 'datetime',
      labels: {
        offsetX: 0,
        formatter: (timestamp) => {
          return format(Number(timestamp), 'MMM dd - h a')
        },
      },
    },
    yaxis: {
      labels: {
        formatter: (value) => String(Math.round(value)),
      },
    },
    tooltip: {
      shared: true,
      intersect: false,
      custom: ({ dataPointIndex, w }) => {
        const countPoint = w.globals.initialSeries[DEVICES_SERIES_INDEX]?.data[dataPointIndex] as DevicesCountPoint | undefined
        const outagePoint = w.globals.initialSeries[OUTAGE_SERIES_INDEX]?.data[dataPointIndex] as OutageChartPoint | undefined
        const isInProgress = devicesCountSeries.endsInProgress && dataPointIndex === devicesCountSeries.data.length - 1
        return buildOutageTooltipHtml({
          title: countPoint ? format(countPoint.x, 'MMM dd - h a') : '',
          deviceCount: String(countPoint?.y ?? '-'),
          isInProgress,
          outage: outagePoint?.meta
            ? {
                expectedCount: String(outagePoint.meta.expectedCount ?? '-'),
                brokenCount: String(outagePoint.meta.brokenCount ?? '-'),
                brokenInUseCount: String(outagePoint.meta.brokenInUseCount ?? '-'),
              }
            : undefined,
        })
      },
    },
  }

  return (
    <Grid container spacing={2} direction='column' wrap="nowrap">
      <Grid item>
        <Typography variant='h6'>Devices network outage</Typography>
      </Grid>
      <Grid item>
        <Typography variant='body2'>
          The chart shows the number of devices from which we received temperature readings for a specific hour.
          The dotted segment is the current hour, which is still collecting readings.
        </Typography>
      </Grid>
      <Grid item container alignItems='center'>
        <Grid item>
          <ToggleButtonGroup
            value={sensorType}
            exclusive
            onChange={handleSensorTypeChange}
            size='small'
          >
            <ToggleButton value='verizon'>CLIMIT GAT</ToggleButton>
            <ToggleButton value='lorawan'>LoRaWAN</ToggleButton>
          </ToggleButtonGroup>
        </Grid>
        <Grid item ml={2}>
          <IconButton aria-label='go-back' color='primary' onClick={handleGoBack} disabled={isAnyRefetching}>
            <ArrowBackIcon />
          </IconButton>
        </Grid>
        <Grid item px={1}>
          <Typography variant='body1'>
            {`${format(dateFrom, 'PP')} - ${format(dateTo, 'PP')}`}
          </Typography>
        </Grid>
        <Grid item>
          <IconButton aria-label='go-forward' color='primary' onClick={handleGoForward} disabled={isLastPeriod || isAnyRefetching}>
            <ArrowForwardIcon />
          </IconButton>
        </Grid>
        <Grid item>
          <IconButton aria-label='reset' color='primary' onClick={resetRange} disabled={isLastPeriod || isAnyRefetching}>
            <CloseIcon />
          </IconButton>
        </Grid>
      </Grid>
      <Grid item container minWidth={0}>
        <Box height='400px' ml={-2} width='calc(100% + 16px)' minWidth={0} position='relative' sx={{ [IN_PROGRESS_SEGMENT_SELECTOR]: { stroke: DEVICES_COUNT_IN_PROGRESS_COLOR } }}>
          {
            isChartLoading && (
              <>
                <Box
                  position='absolute'
                  width='100%'
                  height='100%'
                  zIndex={1}
                  sx={{
                    background: 'grey',
                    opacity: 0.1,
                  }}
                />
                <Box
                  position='absolute'
                  width='100%'
                  height='100%'
                  display='flex'
                  justifyContent='center'
                  alignItems='center'
                >
                  <CircularProgress color='primary' size={26} />
                </Box>
              </>
            )
          }
          <Chart options={chartOptions} data={getChartData()} />
        </Box>
      </Grid>
      {
        selectedOutage && (
          <Grid item>
            <Alert severity='error' onClose={() => setSelectedOutage(null)}>
              {`Network outage at ${format(new Date(selectedOutage.datetime), 'PPpp')}. `}
              {
                selectedOutage.fileName
                  ? <NetworkOutageDownloadLink outageId={selectedOutage.outageId}>Download broken devices list (CSV)</NetworkOutageDownloadLink>
                  : 'Broken devices list is not available for this outage.'
              }
            </Alert>
          </Grid>
        )
      }
      <Grid item>
        <Typography variant='h6'>Network outages</Typography>
      </Grid>
      <Grid item container minWidth={0}>
        <NetworkOutagesTable rows={outagesList} isLoading={isOutagesInitialLoading || isOutagesRefetching} />
      </Grid>
    </Grid>
  )
}
